/**
 * Geographic search for Moss job board — region aliases, stop-word stripping,
 * and multi-field matching (location, locationTags, title).
 */

/** Plain term → tokens that should match in job location corpus. */
export const LOCATION_ALIASES: Readonly<Record<string, readonly string[]>> = {
  texas: ['texas', 'tx', 'dallas', 'fort worth', 'allen', 'mckinney', 'rosebud', 'anson', 'avoca', 'kaufman', 'guthrie', 'dfw'],
  tx: ['texas', 'tx', 'dallas', 'fort worth', 'allen', 'mckinney', 'rosebud', 'anson', 'kaufman', 'guthrie'],
  dallas: ['dallas', 'fort worth', 'dfw', 'allen', 'mckinney', 'texas', 'tx'],
  dfw: ['dallas', 'fort worth', 'dfw', 'allen', 'mckinney', 'texas', 'tx'],
  'fort worth': ['fort worth', 'dallas', 'dfw', 'texas', 'tx'],
  florida: ['florida', 'fl', 'fort lauderdale', 'miami', 'tampa', 'jacksonville', 'pierce', 'okeechobee', 'hendry', 'kenansville', 'clewiston'],
  fl: ['florida', 'fl', 'fort lauderdale', 'miami', 'tampa', 'jacksonville'],
  'south florida': ['fort lauderdale', 'miami', 'florida', 'fl', 'tampa', 'pierce'],
  'fort lauderdale': ['fort lauderdale', 'florida', 'fl', 'south florida'],
  miami: ['miami', 'florida', 'fl', 'south florida'],
  tampa: ['tampa', 'florida', 'fl'],
  colorado: ['colorado', 'co', 'pueblo', 'deer trail', 'platte', 'highland', 'fort morgan', 'belen'],
  co: ['colorado', 'co', 'pueblo', 'deer trail', 'platte'],
  pueblo: ['pueblo', 'colorado', 'co'],
  'new mexico': ['new mexico', 'nm', 'house', 'belen'],
  nm: ['new mexico', 'nm', 'house', 'belen'],
  arizona: ['arizona', 'az'],
  az: ['arizona', 'az'],
  arkansas: ['arkansas', 'ar', 'wilson'],
  ar: ['arkansas', 'ar', 'wilson'],
  kentucky: ['kentucky', 'ky', 'robards', 'providence'],
  ky: ['kentucky', 'ky', 'robards', 'providence'],
  mississippi: ['mississippi', 'ms', 'hollandale'],
  ms: ['mississippi', 'ms', 'hollandale'],
  louisiana: ['louisiana', 'la', 'deridder', 'singer'],
  la: ['louisiana', 'la', 'deridder'],
  hawaii: ['hawaii', 'hi', 'honolulu'],
  hi: ['hawaii', 'hi', 'honolulu'],
  oklahoma: ['oklahoma', 'ok'],
  ok: ['oklahoma', 'ok'],
  georgia: ['georgia', 'ga'],
  ga: ['georgia', 'ga'],
  'north carolina': ['north carolina', 'nc'],
  nc: ['north carolina', 'nc'],
  remote: ['remote', 'hybrid'],
  travel: ['travel', 'multi-state', 'multi state'],
};

const SEARCH_STOP_WORDS = new Set([
  'a',
  'an',
  'the',
  'in',
  'on',
  'at',
  'for',
  'to',
  'of',
  'and',
  'or',
  'with',
  'jobs',
  'job',
  'roles',
  'role',
  'openings',
  'opening',
  'positions',
  'position',
  'area',
  'areas',
  'near',
  'around',
  'any',
  'anything',
  'something',
  'show',
  'me',
  'find',
  'looking',
  'work',
  'moss',
  'there',
  'are',
  'is',
  'what',
  'whats',
  "what's",
  'where',
  'available',
  'hiring',
  'tell',
  'about',
  'have',
  'has',
  'got',
  'do',
  'does',
  'can',
  'could',
  'would',
  'please',
  'kind',
  'type',
  'types',
  'some',
  'list',
  'give',
]);

const MULTI_WORD_LOCATION_PHRASES = Object.keys(LOCATION_ALIASES)
  .filter((k) => k.includes(' '))
  .sort((a, b) => b.length - a.length);

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Pull known multi-word regions out as single tokens (e.g. "south florida"). */
function extractLocationPhrases(input: string): { remainder: string; phrases: string[] } {
  let remainder = input.trim().toLowerCase();
  const phrases: string[] = [];
  for (const phrase of MULTI_WORD_LOCATION_PHRASES) {
    const re = new RegExp(`\\b${escapeRegExp(phrase).replace(/\s+/g, '\\s+')}\\b`, 'gi');
    if (!re.test(remainder)) continue;
    phrases.push(phrase);
    remainder = remainder.replace(re, ' ');
  }
  return { remainder, phrases };
}

/** Tokens for search/location matching — strips filler words. */
export function normalizeSearchTokens(query: string): string[] {
  const { remainder, phrases } = extractLocationPhrases(query.trim().toLowerCase());
  const splitTokens = remainder
    .split(/[\s,;/]+/)
    .map((t) => t.replace(/^[^a-z0-9$]+|[^a-z0-9$]+$/g, ''))
    .filter((t) => t.length > 1 && !SEARCH_STOP_WORDS.has(t));
  return [...phrases, ...splitTokens];
}

/** True when token is a known geographic key or alias. */
export function isGeographicToken(token: string): boolean {
  const lc = token.toLowerCase();
  if (LOCATION_ALIASES[lc]) return true;
  for (const aliases of Object.values(LOCATION_ALIASES)) {
    if (aliases.some((a) => a === lc)) return true;
  }
  return false;
}

/**
 * When the voice model puts geography in `search`, split into location + role terms.
 * e.g. "hourly solar jobs in Texas" → location: "Texas", search: "hourly solar"
 */
export function splitSearchAndLocation(rawSearch: string): {
  location?: string;
  search?: string;
} {
  const trimmed = rawSearch.trim();
  if (!trimmed) return {};

  const tokens = normalizeSearchTokens(trimmed);
  if (tokens.length === 0) return {};

  const geoTokens = tokens.filter((t) => isGeographicToken(t));
  const roleTokens = tokens.filter((t) => !isGeographicToken(t));

  if (geoTokens.length === 0) {
    return { search: trimmed };
  }

  return {
    location: geoTokens.join(' '),
    search: roleTokens.length > 0 ? roleTokens.join(' ') : undefined,
  };
}

/** All alias strings for a token (includes the token itself). */
export function aliasesForToken(token: string): readonly string[] {
  const lc = token.toLowerCase();
  const direct = LOCATION_ALIASES[lc];
  if (direct) return direct;
  // Reverse lookup: token appears in some alias list
  for (const aliases of Object.values(LOCATION_ALIASES)) {
    if (aliases.some((a) => a === lc || lc.includes(a) || a.includes(lc))) {
      return aliases;
    }
  }
  return [lc];
}

export interface LocationSearchable {
  title: string;
  location?: string;
  locationTags?: readonly string[];
  description?: string;
  requirements?: string | null;
}

/** Build normalized geographic corpus for a job. */
export function getLocationCorpus(job: LocationSearchable): string {
  return [
    job.title,
    job.location ?? '',
    ...(job.locationTags ?? []),
    job.description ?? '',
    job.requirements ?? '',
  ]
    .join(' ')
    .toLowerCase();
}

/** True if alias appears in corpus — word boundaries for short tokens (state abbrev). */
function corpusIncludesAlias(corpus: string, alias: string): boolean {
  if (alias.length <= 3) {
    const escaped = alias.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`\\b${escaped}\\b`, 'i').test(corpus);
  }
  return corpus.includes(alias);
}

/** True when every meaningful token matches location corpus (with alias expansion). */
export function jobMatchesLocationQuery(job: LocationSearchable, query: string): boolean {
  const tokens = normalizeSearchTokens(query);
  if (tokens.length === 0) return true;
  const corpus = getLocationCorpus(job);
  return tokens.every((token) => {
    const aliases = aliasesForToken(token);
    return aliases.some((alias) => corpusIncludesAlias(corpus, alias));
  });
}

/** Combined text search — location-aware token matching on full job text. */
export function jobMatchesSearchQuery(
  job: LocationSearchable & { department?: string; track?: string; team?: string; type?: string; payRange?: string; skillMatches?: string[] },
  query: string,
): boolean {
  const tokens = normalizeSearchTokens(query);
  if (tokens.length === 0) return true;
  const corpus = [
    getLocationCorpus(job),
    job.department ?? '',
    job.track ?? '',
    job.team ?? '',
    job.type ?? '',
    job.payRange ?? '',
    ...(job.skillMatches ?? []),
  ]
    .join(' ')
    .toLowerCase();

  return tokens.every((token) => {
    if (corpusIncludesAlias(corpus, token)) return true;
    return aliasesForToken(token).some((alias) => corpusIncludesAlias(corpus, alias));
  });
}
